// Admin Institutions Management Component
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const InstitutionsManagement = () => {
    const [institutions, setInstitutions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        country: '',
        website: '',
        is_active: true,
        sort_order: 0
    });

    const API_BASE = process.env.REACT_APP_API_URL;
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

    if (!API_BASE || !BACKEND_URL) {
        console.error('❌ CRITICAL: API URLs not configured in InstitutionsManagement!');
    }

    useEffect(() => {
        fetchInstitutions();
    }, []);

    const fetchInstitutions = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${API_BASE}/institutions`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setInstitutions(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching institutions:', error);
            toast.error('Failed to fetch institutions');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const token = localStorage.getItem('adminToken');
        const data = new FormData();
        data.append('name', formData.name);
        data.append('country', formData.country);
        data.append('website', formData.website);
        data.append('is_active', formData.is_active ? 1 : 0);
        data.append('sort_order', formData.sort_order);
        if (logoFile) {
            data.append('logo', logoFile);
        }

        try {
            if (editingId) {
                await axios.put(`${API_BASE}/institutions/${editingId}`, data, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
                toast.success('Institution updated successfully!');
            } else {
                await axios.post(`${API_BASE}/institutions`, data, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
                toast.success('Institution added successfully!');
            }

            resetForm();
            fetchInstitutions();
        } catch (error) {
            console.error('Error saving institution:', error);
            toast.error(error.response?.data?.message || 'Failed to save institution');
        }
    };

    const handleEdit = (institution) => {
        setEditingId(institution.id);
        setFormData({
            name: institution.name,
            country: institution.country,
            website: institution.website || '',
            is_active: institution.is_active,
            sort_order: institution.sort_order || 0
        });
        setLogoPreview(institution.logo ? `${BACKEND_URL}${institution.logo}` : null);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this institution?')) return;

        try {
            const token = localStorage.getItem('adminToken');
            await axios.delete(`${API_BASE}/institutions/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Institution deleted successfully!');
            fetchInstitutions();
        } catch (error) {
            console.error('Error deleting institution:', error);
            toast.error('Failed to delete institution');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            country: '',
            website: '',
            is_active: true,
            sort_order: 0
        });
        setLogoFile(null);
        setLogoPreview(null);
        setEditingId(null);
        setShowForm(false);
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-success" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="institutions-management">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 style={{ color: '#6B8E23' }}>RT Institutions Management</h3>
                <button
                    className="btn btn-success"
                    style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}
                    onClick={() => setShowForm(!showForm)}
                >
                    {showForm ? 'Cancel' : '+ Add Institution'}
                </button>
            </div>

            {/* Form */}
            {showForm && (
                <div className="card mb-4 shadow-sm">
                    <div className="card-body">
                        <h5 className="card-title mb-3">{editingId ? 'Edit Institution' : 'Add New Institution'}</h5>
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Institution Name *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Country *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="country"
                                        value={formData.country}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Website</label>
                                    <input
                                        type="url"
                                        className="form-control"
                                        name="website"
                                        value={formData.website}
                                        onChange={handleInputChange}
                                        placeholder="https://example.com"
                                    />
                                </div>
                                <div className="col-md-3 mb-3">
                                    <label className="form-label">Sort Order</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="sort_order"
                                        value={formData.sort_order}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="col-md-3 mb-3">
                                    <label className="form-label">Active</label>
                                    <div className="form-check mt-2">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            name="is_active"
                                            checked={formData.is_active}
                                            onChange={handleInputChange}
                                        />
                                        <label className="form-check-label">Active</label>
                                    </div>
                                </div>
                                <div className="col-md-12 mb-3">
                                    <label className="form-label">Logo</label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept="image/*"
                                        onChange={handleLogoChange}
                                    />
                                    {logoPreview && (
                                        <div className="mt-2">
                                            <img src={logoPreview} alt="Preview" style={{ maxHeight: '100px', maxWidth: '200px', objectFit: 'contain' }} />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="d-flex gap-2">
                                <button type="submit" className="btn btn-success" style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}>
                                    {editingId ? 'Update Institution' : 'Add Institution'}
                                </button>
                                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Institutions List */}
            <div className="card shadow-sm">
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead style={{ backgroundColor: '#f8f9fa' }}>
                                <tr>
                                    <th>Logo</th>
                                    <th>Name</th>
                                    <th>Country</th>
                                    <th>Website</th>
                                    <th>Status</th>
                                    <th>Sort</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {institutions.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="text-center text-muted py-4">
                                            No institutions found. Add your first institution!
                                        </td>
                                    </tr>
                                ) : (
                                    institutions.map((institution) => (
                                        <tr key={institution.id}>
                                            <td>
                                                {institution.logo ? (
                                                    <img
                                                        src={institution.logo.startsWith('http') ? institution.logo : `${BACKEND_URL}${institution.logo}`}
                                                        alt={institution.name}
                                                        style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                                                    />
                                                ) : (
                                                    <div style={{ width: '50px', height: '50px', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <small>No logo</small>
                                                    </div>
                                                )}
                                            </td>
                                            <td>{institution.name}</td>
                                            <td>{institution.country}</td>
                                            <td>
                                                {institution.website ? (
                                                    <a href={institution.website} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                                                        Visit
                                                    </a>
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                            <td>
                                                <span className={`badge ${institution.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                                    {institution.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>{institution.sort_order}</td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-primary me-2"
                                                    onClick={() => handleEdit(institution)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleDelete(institution.id)}
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InstitutionsManagement;
